import React, { useState } from 'react';
import { useEffect } from 'react';
import { X, Bug, Zap, FileText, BookOpen, CheckSquare, Flag, Calendar, User, Clock, Upload, Image, File, Trash2 } from 'lucide-react';
import { UserAvatar } from '../UI/UserAvatar';
import { Issue, User as UserType, Project, ChatMessage } from '../../types';
import { MultiAssigneeSelector } from '../Issues/MultiAssigneeSelector';
import { IssueChat } from '../Chat/IssueChat';
import { TimelineGantt } from '../Timeline/TimelineGantt';
import { apiService } from '../../services/api';
import { toast } from '../UI/Toast';
import { compressImage } from '../../utils/file';

interface IssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (issueData: Omit<Issue, 'id' | 'key' | 'createdAt' | 'updatedAt' | 'subtasks' | 'attachments' | 'comments' | 'worklog'>) => void;
  users: UserType[];
  projects: Project[];
  issue?: Issue;
  currentUser?: UserType;
}

export const IssueModal: React.FC<IssueModalProps> = ({
  isOpen,
  onClose,
  onSave,
  users,
  projects,
  issue,
  currentUser
}) => {
  const [projectMembers, setProjectMembers] = useState<UserType[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isLoadingIssue, setIsLoadingIssue] = useState(false);
  const [formData, setFormData] = useState({
    projectId: issue?.projectId || projects[0]?.id || '',
    summary: issue?.summary || '',
    description: issue?.description || '',
    type: issue?.type || 'task',
    status: issue?.status || 'to-do',
    priority: issue?.priority || 'medium',
    assignee: issue?.assignee || '',
    assignees: issue?.assignees || [],
    reporter: issue?.reporter || users[0]?.id || '',
    labels: issue?.labels || [],
    components: issue?.components || [],
    fixVersions: issue?.fixVersions || [],
    storyPoints: issue?.storyPoints || undefined,
    dueDate: issue?.dueDate || '',
    sprint: issue?.sprint || '',
    epic: issue?.epic || '',
    estimationTime: issue?.estimationTime || undefined,
    assignmentHistory: issue?.assignmentHistory || []
  });

  const currentProjectObj = projects.find(p => p.id === (issue?.projectId || formData.projectId));
  const activeProjectMembers = (projectMembers || []).filter(user => {
    if (!user.active) return false;
    if (currentProjectObj && currentProjectObj.teamMembers) {
      return currentProjectObj.teamMembers.includes(user.id);
    }
    return true;
  });

  const [newLabel, setNewLabel] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>(issue?.attachments || []);
  const [attachmentPreviews, setAttachmentPreviews] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>({});
  const [preSelectedProjectId, setPreSelectedProjectId] = useState<string>('');
  const [apiIssueData, setApiIssueData] = useState<any>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const isReporter = currentUser?.id === issue?.reporter;
  const isAssignee = issue?.assignees?.includes(currentUser?.id || '') ?? false;

  // Load project members when project changes
  useEffect(() => {
    if (formData.projectId) {
      loadProjectMembers(formData.projectId);
    }
  }, [formData.projectId]);

  useEffect(() => {
    if (isOpen) {
      if (issue) {
        // Load issue details from API when editing
        loadIssueDetails(issue.id);
      } else {
        // Reset form for new issue
        setFormData({
          projectId: preSelectedProjectId || projects[0]?.id || '',
          summary: '',
          description: '',
          type: 'task',
          status: 'to-do',
          priority: 'medium',
          assignee: '',
          assignees: [],
          reporter: currentUser?.id || users[0]?.id || '',
          labels: [],
          components: [],
          fixVersions: [],
          storyPoints: undefined,
          dueDate: '',
          sprint: '',
          epic: '',
          estimationTime: undefined,
          assignmentHistory: []
        });
      }
      setNewLabel('');
      setAttachments([]);
      setExistingAttachments(issue?.attachments || []);
      setUploadingFiles(false);
    }
  }, [isOpen, issue, preSelectedProjectId, projects, currentUser, users]);

  useEffect(() => {
    const urls = attachments.map(file => URL.createObjectURL(file));
    setAttachmentPreviews(urls);
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [attachments]);

  const loadProjectMembers = async (projectId: string) => {
    try {
      setIsLoadingMembers(true);
      const members = await apiService.getProjectMembers(projectId);
      setProjectMembers(members);
    } catch (error) {
      console.error('Failed to load project members:', error);
      // Fallback to all users if API fails
      setProjectMembers(users);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!(isReporter || isAssignee)) {
      toast.error('Only the reporter or assignees can update this issue.');
      return;
    }

    const saveIssue = async () => {
      try {
        setIsLoadingIssue(true);

        // Show loading toast
        toast.loading('Updating issue...', { id: 'update-issue' });

        // Save the issue first
        onSave(formData);

        // Upload new attachments if any
        if (attachments.length > 0 && issue) {
          try {
            setUploadingFiles(true);
            toast.loading('Uploading attachments...', { id: 'update-issue' });
            await apiService.uploadIssueAttachments(issue.id, attachments);
            setAttachments([]);
          } catch (uploadError) {
            console.error('Failed to upload attachments:', uploadError);
            toast.error('Issue updated but some attachments failed to upload.', { id: 'update-issue' });
          } finally {
            setUploadingFiles(false);
          }
        }

        // Email notifications are already sent by the backend when the issue is updated,
        // so we don't need to call sendIssueUpdateNotifications here (it would cause duplicate emails).
        toast.success('Issue updated successfully!', { id: 'update-issue' });

        onClose();
      } catch (error) {
        console.error('Failed to save issue:', error);
        setErrors({ submit: 'Failed to save issue. Please try again.' });
        toast.error('Failed to update issue. Please try again.', { id: 'update-issue' });
      } finally {
        setIsLoadingIssue(false);
      }
    };

    saveIssue();
  };

  const loadIssueDetails = async (issueId: string) => {
    try {
      setIsLoadingIssue(true);
      console.log('Loading issue details for:', issueId);

      // Call API to get fresh issue details
      const issueDetails = await apiService.getIssue(issueId);
      console.log('Loaded issue details:', issueDetails);

      // Update form with API data
      setFormData({
        projectId: issueDetails.project_id || issueDetails.projectId,
        summary: issueDetails.summary || '',
        description: issueDetails.description || '',
        type: issueDetails.type || 'task',
        status: issueDetails.status || 'to-do',
        priority: issueDetails.priority || 'medium',
        assignee: issueDetails.assignee || '',
        assignees: issueDetails.assignees || [],
        reporter: issueDetails.reporter || '',
        labels: issueDetails.labels || [],
        components: issueDetails.components || [],
        fixVersions: issueDetails.fix_versions || issueDetails.fixVersions || [],
        storyPoints: issueDetails.story_points || issueDetails.storyPoints,
        dueDate: issueDetails.due_date || issueDetails.dueDate || '',
        sprint: issueDetails.sprint || '',
        epic: issueDetails.epic || '',
        estimationTime: issueDetails.estimation_time || issueDetails.estimationTime,
        assignmentHistory: issueDetails.assignmentHistory || []
      });

      // Store API response data for display purposes
      setApiIssueData(issueDetails);
      setExistingAttachments(issueDetails.attachments || []);

    } catch (error) {
      console.error('Failed to load issue details:', error);
      setErrors({ submit: 'Failed to load issue details. Using cached data.' });

      // Fallback to passed issue data
      if (issue) {
        setFormData({
          projectId: issue.projectId,
          summary: issue.summary || '',
          description: issue.description || '',
          type: issue.type || 'task',
          status: issue.status || 'to-do',
          priority: issue.priority || 'medium',
          assignee: issue.assignee || '',
          assignees: issue.assignees || [],
          reporter: issue.reporter || '',
          labels: issue.labels || [],
          components: issue.components || [],
          fixVersions: issue.fixVersions || [],
          storyPoints: issue.storyPoints,
          dueDate: issue.dueDate || '',
          sprint: issue.sprint || '',
          epic: issue.epic || '',
          estimationTime: issue.estimationTime,
          assignmentHistory: issue.assignmentHistory || []
        });
      }
    } finally {
      setIsLoadingIssue(false);
    }
  };

  const handleAssigneesChange = (assignees: string[]) => {
    setFormData({
      ...formData,
      assignees
    });
    // Note: No API calls here - only update local state
  };

  const addLabel = () => {
    if (newLabel.trim() && !formData.labels.includes(newLabel.trim())) {
      setFormData({
        ...formData,
        labels: [...formData.labels, newLabel.trim()]
      });
      setNewLabel('');
    }
  };

  const removeLabel = (label: string) => {
    setFormData({
      ...formData,
      labels: formData.labels.filter(l => l !== label)
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingFiles(true);

    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
      'application/pdf',
      'text/plain', 'text/csv',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/zip', 'application/x-zip-compressed',
      'application/json', 'application/xml'
    ];

    const processedFiles: File[] = [];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, attachments: `File type "${file.type}" is not supported.` }));
        continue;
      }
      let processed = file;
      if (file.type.startsWith('image/')) {
        processed = await compressImage(file);
      }
      const maxSize = 10 * 1024 * 1024; // 10MB after compression
      if (processed.size > maxSize) {
        setErrors(prev => ({ ...prev, attachments: `File "${file.name}" is too large. Maximum size is 10MB.` }));
        continue;
      }
      processedFiles.push(processed);
    }

    if (processedFiles.length > 0) {
      setAttachments(prev => [...prev, ...processedFiles]);
      setErrors(prev => ({ ...prev, attachments: undefined }));
    }

    setUploadingFiles(false);
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = (index: number) => {
    setExistingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="w-4 h-4 text-blue-600" />;
    } else if (file.type === 'application/pdf') {
      return <File className="w-4 h-4 text-red-600" />;
    } else if (file.type.includes('sheet') || file.type.includes('excel')) {
      return <File className="w-4 h-4 text-green-600" />;
    } else if (file.type.includes('document') || file.type.includes('word')) {
      return <File className="w-4 h-4 text-blue-600" />;
    } else if (file.type.includes('presentation') || file.type.includes('powerpoint')) {
      return <File className="w-4 h-4 text-orange-600" />;
    }
    return <File className="w-4 h-4 text-gray-600" />;
  };

  const getExistingFileIcon = (attachment: any) => {
    if (attachment.mimeType?.startsWith('image/')) {
      return <Image className="w-4 h-4 text-blue-600" />;
    } else if (attachment.mimeType === 'application/pdf') {
      return <File className="w-4 h-4 text-red-600" />;
    } else if (attachment.mimeType?.includes('sheet') || attachment.mimeType?.includes('excel')) {
      return <File className="w-4 h-4 text-green-600" />;
    } else if (attachment.mimeType?.includes('document') || attachment.mimeType?.includes('word')) {
      return <File className="w-4 h-4 text-blue-600" />;
    } else if (attachment.mimeType?.includes('presentation') || attachment.mimeType?.includes('powerpoint')) {
      return <File className="w-4 h-4 text-orange-600" />;
    }
    return <File className="w-4 h-4 text-gray-600" />;
  };

  const getIssueTypeIcon = (type: string) => {
    switch (type) {
      case 'bug':
        return <Bug className="w-4 h-4 text-red-600" />;
      case 'story':
        return <Zap className="w-4 h-4 text-green-600" />;
      case 'task':
        return <CheckSquare className="w-4 h-4 text-blue-600" />;
      case 'epic':
        return <BookOpen className="w-4 h-4 text-purple-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const handleSendChatMessage = async (
    content: string,
    mentions?: string[],
    fileUrl?: string,
    fileName?: string,
    fileSize?: number,
    messageType?: string
  ) => {
    if (!issue) return;
    try {
      const newMessage = await apiService.sendChatMessage(issue.id, {
        content,
        mentions,
        fileUrl,
        fileName,
        fileSize,
        messageType,
      });
      setChatMessages(prev => [...prev, newMessage]);
    } catch (error) {
      console.error('Failed to send message', error);
      toast.error('Failed to send message');
    }
  };

  const handleDelete = async () => {
    if (!issue || !onDeleteIssue) return;
    if (!window.confirm(`Are you sure you want to delete "${issue.summary}"? This action cannot be undone.`)) return;
    try {
      await onDeleteIssue(issue.id);
      toast.success('Issue deleted successfully');
      onClose();
    } catch (error) {
      console.error('Failed to delete issue:', error);
      toast.error('Failed to delete issue');
    }
  };

  const handleSendSlackReminder = async () => {
    if (!issue) return;
    try {
      setIsSendingReminder(true);
      toast.loading('Sending Slack reminder...', { id: 'slack-reminder' });
      await apiService.sendSlackReminder(issue.id);
      toast.success('Slack reminder sent successfully!', { id: 'slack-reminder' });
    } catch (error) {
      console.error('Failed to send Slack reminder:', error);
      toast.error('Failed to send Slack reminder.', { id: 'slack-reminder' });
    } finally {
      setIsSendingReminder(false);
    }
  };

  useEffect(() => {
    if (issue) {
      apiService
        .getChatMessages(issue.id)
        .then(setChatMessages)
        .catch(err => console.error('Failed to load messages', err));
    }
  }, [issue]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex overflow-hidden">
          {/* Main Issue Form */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  {issue ? `Edit Issue - ${issue.key}` : 'Create Issue'}
                </h2>
                {issue && (
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowChat(!showChat)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${showChat
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {showChat ? 'Hide Chat' : 'Show Chat'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSendSlackReminder}
                      disabled={isSendingReminder}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors flex items-center space-x-1 disabled:opacity-50"
                    >
                      <span>⏰ Remind on Slack</span>
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-3">
                {issue && (
                  <>
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isLoadingIssue}
                      className="px-4 py-2 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      form="issue-form"
                      className="px-6 py-2 text-xs bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      disabled={isLoadingIssue || (!isReporter && !isAssignee)}
                    >
                      {isLoadingIssue ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Loading...</span>
                        </>
                      ) : (
                        <span>Update Issue</span>
                      )}
                    </button>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Loading State */}
              {isLoadingIssue && (
                <div className="p-6 text-center">
                  <div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 text-xs">Loading issue details...</p>
                </div>
              )}

              <form id="issue-form" onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Main Content */}
                  <div className="lg:col-span-2 space-y-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Project *
                      </label>
                      <select
                        required
                        value={formData.projectId}
                        onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        disabled={!isReporter}
                      >
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name} ({project.key})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Issue Type *
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['story', 'task', 'bug', 'epic'].map((type) => (
                          <label
                            key={type}
                            className={`flex items-center space-x-2 p-2.5 border rounded-lg cursor-pointer transition-colors ${formData.type === type
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-300 hover:bg-gray-50'
                              }`}
                            style={{ pointerEvents: !isReporter ? 'none' : undefined, opacity: !isReporter ? 0.5 : 1 }}
                          >
                            <input
                              type="radio"
                              name="type"
                              value={type}
                              checked={formData.type === type}
                              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                              className="sr-only"
                            />
                            {getIssueTypeIcon(type)}
                            <span className="text-xs font-medium capitalize">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Summary *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.summary}
                        onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter a brief summary of the issue"
                        disabled={!isReporter}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={10}
                        className="w-full px-3 py-2 text-xs leading-relaxed border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Provide a detailed description of the issue"
                        disabled={!isReporter}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Labels
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.labels.map((label) => (
                          <span
                            key={label}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-2xs font-medium bg-green-100 text-green-800"
                          >
                            {label}
                            <button
                              type="button"
                              onClick={() => removeLabel(label)}
                              className="ml-1 text-green-600 hover:text-green-800"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newLabel}
                          onChange={(e) => setNewLabel(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLabel())}
                          className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Add a label"
                        />
                        <button
                          type="button"
                          onClick={addLabel}
                          className="px-4 py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Attachments
                      </label>

                      {errors.attachments && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-700 text-xs">{errors.attachments}</p>
                        </div>
                      )}

                      {/* Existing Attachments */}
                      {existingAttachments.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-xs font-semibold text-gray-500 mb-2">Current Attachments</h5>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {existingAttachments.map((attachment, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
                                <a
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-3"
                                >
                                  {attachment.mimeType?.startsWith('image/') ? (
                                    <img
                                      src={attachment.url}
                                      alt={attachment.filename}
                                      className="w-10 h-10 object-cover rounded"
                                    />
                                  ) : (
                                    getExistingFileIcon(attachment)
                                  )}
                                  <div>
                                    <p className="text-xs font-medium text-blue-900 truncate max-w-xs">
                                      {attachment.filename}
                                    </p>
                                    <p className="text-2xs text-blue-700">
                                      {formatFileSize(attachment.size)} • {attachment.mimeType}
                                    </p>
                                  </div>
                                </a>
                                {isReporter && (
                                  <button
                                    type="button"
                                    onClick={() => removeExistingAttachment(index)}
                                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Remove file"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {isReporter && (
                        <>
                          {/* File Upload Area */}
                          <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${uploadingFiles
                            ? 'border-green-400 bg-green-50'
                            : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
                            }`}>
                            <input
                              type="file"
                              multiple
                              accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx,.zip,.json,.xml"
                              onChange={handleFileUpload}
                              className="hidden"
                              id="file-upload"
                              disabled={uploadingFiles}
                            />
                            <label htmlFor="file-upload" className={`cursor-pointer ${uploadingFiles ? 'cursor-not-allowed' : ''}`}>
                              {uploadingFiles ? (
                                <div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                              ) : (
                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                              )}
                              <p className="text-xs text-gray-600 mb-1">
                                <span className="text-green-600 font-medium">
                                  {uploadingFiles ? 'Processing files...' : 'Click to upload'}
                                </span>
                                {!uploadingFiles && ' or drag and drop'}
                              </p>
                              <p className="text-2xs text-gray-500">
                                Images, PDFs, Office Documents, Archives (Max 10MB each)
                              </p>
                            </label>
                          </div>

                          {/* New Attachments List */}
                          {attachments.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <div className="flex items-center justify-between">
                                <h4 className="text-xs font-semibold text-gray-700">
                                  New Attachments ({attachments.length})
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => setAttachments([])}
                                  className="text-2xs text-red-600 hover:text-red-800 font-medium"
                                >
                                  Remove All
                                </button>
                              </div>
                              <div className="space-y-2 max-h-32 overflow-y-auto">
                                {attachments.map((file, index) => {
                                  const previewUrl = attachmentPreviews[index];
                                  const isImage = file.type.startsWith('image/');
                                  return (
                                    <div
                                      key={index}
                                      className="flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => window.open(previewUrl, '_blank')}
                                        className="flex items-center space-x-3 text-left"
                                      >
                                        {isImage ? (
                                          <img
                                            src={previewUrl}
                                            alt={file.name}
                                            className="w-10 h-10 object-cover rounded"
                                          />
                                        ) : (
                                          getFileIcon(file)
                                        )}
                                        <div>
                                          <p className="text-xs font-medium text-green-900 truncate max-w-xs">
                                            {file.name}
                                          </p>
                                          <p className="text-2xs text-green-700">
                                            {formatFileSize(file.size)} • {file.type}
                                          </p>
                                        </div>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => removeAttachment(index)}
                                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                        title="Remove file"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="backlog">Backlog</option>
                        <option value="to-do">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="in-review">In Review</option>
                        <option value="on-hold">On Hold</option>
                        <option value="in-review">In Review</option>
                        <option value="done">Done</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        disabled={!isReporter}
                      >
                        <option value="lowest">Lowest</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="highest">Highest</option>
                      </select>
                    </div>

                    <MultiAssigneeSelector
                      users={activeProjectMembers}
                      selectedAssignees={formData.assignees}
                      onAssigneesChange={handleAssigneesChange}
                      assignmentHistory={formData.assignmentHistory}
                      currentUserId={currentUser?.id || ''}
                      issueKey={issue?.key}
                      apiAssigneesUsers={apiIssueData?.assignees_users}
                      disabled={!isReporter}
                    />

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Reporter
                      </label>
                      {apiIssueData?.reporter_user ? (
                        <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <UserAvatar
                              name={apiIssueData.reporter_user.display_name}
                              src={apiIssueData.reporter_user.avatar_url}
                              className="w-6 h-6 border border-gray-200"
                            />
                            <span className="text-xs text-gray-900 font-medium">
                              {apiIssueData.reporter_user.display_name}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <select
                          value={formData.reporter}
                          onChange={(e) => setFormData({ ...formData, reporter: e.target.value })}
                          className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          {activeProjectMembers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.displayName}
                            </option>
                          ))}
                        </select>
                      )}
                      {isLoadingMembers && (
                        <p className="text-2xs text-gray-500 mt-1">Loading project members...</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Story Points
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.storyPoints || ''}
                        onChange={(e) => setFormData({ ...formData, storyPoints: e.target.value ? parseInt(e.target.value) : undefined })}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="0"
                        disabled={!isReporter}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Estimation Time (Hours)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.estimationTime || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          estimationTime: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter hours (e.g., 8 or 8.5)"
                      />
                      <p className="text-2xs text-gray-500 mt-1">
                        Enter estimated hours (supports decimals like 2.5 for 2 hours 30 minutes)
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Timeline Section - Only show when editing an existing issue */}
                {issue && (
                  <TimelineGantt
                    issueId={issue.id}
                    canEdit={isReporter || isAssignee}
                    users={activeProjectMembers}
                    issueDueDate={formData.dueDate}
                  />
                )}

                <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                  {isReporter && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isLoadingIssue}
                      className="px-4 py-2 text-xs bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoadingIssue}
                    className="px-4 py-2 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="issue-form"
                    disabled={isLoadingIssue || uploadingFiles || (!isReporter && !isAssignee)}
                    className="px-6 py-2 text-xs bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isLoadingIssue ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Updating...</span>
                      </>
                    ) : uploadingFiles ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processing Files...</span>
                      </>
                    ) : (
                      <span>Update Issue</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Issue Chat */}
      {showChat && issue && currentUser && (
        <IssueChat
          issue={issue}
          messages={chatMessages}
          users={users}
          currentUser={currentUser}
          onSendMessage={handleSendChatMessage}
          isMinimized={isChatMinimized}
          onToggleMinimize={() => setIsChatMinimized(!isChatMinimized)}
          onClose={() => setShowChat(false)}
        />
      )}
    </>
  );
};
